import React, { useEffect, useMemo, useState, useRef } from 'react';
import './RoutinePanel.scss';

import { useForm } from '../../hooks/useForm';
import { classNames } from 'primereact/utils';
import { useClient } from '../../hooks/useClient';
import { Routine, ROUTINE_TYPES, RoutineExerciseHashMap } from '../../types/Fisiofit/routine';
import { add, addDays, format } from 'date-fns';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { useLocaleContext } from '../../context/LocaleContext';
import { Panel } from '../Panel/Panel';
import { ROUTINE_FORM_VALIDATORS, RoutineForm } from './validators';
import { confirmDialog } from 'primereact/confirmdialog';
import { getRoutineDays, getRoutineDaysSeverity, getRoutineDeleteConfirmationDialogProps } from '../../pages/Routines/Routines';
import { useFeaturesToggle } from '../../hooks/useFeaturesToggle';
import { ExerciseShort } from '../../types/Fisiofit/exercise';
import useSWR from 'swr';
import { useAuthContext } from '../../context/AuthContext';

import { MUSCLE_GROUPS, EXERCISE_CATEGORIES } from '../../types/Fisiofit/fisiofitConst';
import { DayItemSharedProps, RoutinePanelProps } from './types';
import { ExerciseDaysField } from './days/ExerciseDaysField';
import { RoutinePanelHeader } from './RoutinePanelHeader';
import { RoutineTypeField } from './fields/RoutineTypeField';
import { RoutineNameField } from './fields/RoutineNameField';
import { RoutineUserField } from './fields/RoutineUserField';
import { StartDateField } from './fields/StartDateField';
import { DurationField } from './fields/DurationField';
import { RoutineStatusField } from './fields/RoutineStatusField';
import { RoutineDescriptionField } from './fields/RoutineDescriptionField';
import { CopyRoutineField } from './fields/CopyRoutineField';
import { useRoutineExercises } from './hooks/useRoutineExercises';
import { useRoutineSupersets } from './hooks/useRoutineSupersets';
import { useRoutineDays } from './hooks/useRoutineDays';
import { useRoutineDragEnd } from './hooks/useRoutineDragEnd';
import { useRoutineRequests } from './hooks/useRoutineRequests';
import { getInitialRoutineForm, getRoutineDisplayName, getRoutinePanelTitle } from './utils/form';

export const RoutinePanel = ({
  resource,
  visible,
  onHide,
  onIsEditingChange,
  onDeleteResource,
  onSaveSuccess,
  isEditing = false,
  isDeleting = false,
  overrideNextRoutine = false,
  creatingFromUserProfile = false,
  allowedFeatures,
}: RoutinePanelProps) => {
  const [currentRoutine, setCurrentRoutine] = useState<Routine | undefined>(resource);
  const [creatingNewRoutineForExpired, setCreatingNewRoutineForExpired] = useState(false);
  const { post, patch, get } = useClient();
  const { user } = useAuthContext();
  const { organization } = useOrganizationContext();
  const { locale } = useLocaleContext();
  const isCreating = !currentRoutine?.id;
  const isNextRoutine = currentRoutine !== resource && !creatingNewRoutineForExpired;
  const hasNextRoutine = !!currentRoutine?.nextRoutine;
  const userHasEditFeature = allowedFeatures?.edit ? useFeaturesToggle(allowedFeatures?.edit) : true;
  const exerciseHeaderRef = useRef<HTMLDivElement>(null);
  const [exerciseListWidth, setExerciseListWidth] = useState(0);
  // Estado para controlar los ejercicios expandidos
  const [expandedExerciseIds, setExpandedExerciseIds] = useState<Record<string, boolean>>({});

  const [selectedExerciseMuscleGroups, setSelectedExerciseMuscleGroups] = useState<MUSCLE_GROUPS[]>([]);
  const [selectedExerciseCategories, setSelectedExerciseCategories] = useState<EXERCISE_CATEGORIES[]>([]);

  // Obtener la lista de ejercicios para el selector
  const { data: organizationExercises } = useSWR(
    (isEditing || isCreating) && organization?.id ? `/organizations/${organization?.id}/exercises?short=true` : null,
    get<ExerciseShort[]>,
  );

  const initialRoutineForm = useMemo(() => getInitialRoutineForm(currentRoutine), [currentRoutine]);
  const { form, setFormFields, setForm, isSaving, setIsSaving, hasChanged, validationErrors, resetForm, validate } =
    useForm<RoutineForm>(initialRoutineForm, !visible, ROUTINE_FORM_VALIDATORS);

  const { daysDifference, elapsedDays, hasStarted } = getRoutineDays(form?.localStartDate, form?.duration);
  const formattedStartDate = form?.localStartDate ? format(new Date(form.localStartDate), "d 'de' MMMM") : null;
  const formattedEndDate =
    form?.localStartDate && form?.duration
      ? format(add(new Date(form.localStartDate), { days: form.duration }), "d 'de' MMMM")
      : null;
  const hasEnded = !!currentRoutine?.duration && daysDifference !== null && daysDifference > currentRoutine?.duration;
  const canEditStartDate = !isNextRoutine && (isCreating || (isEditing && !hasStarted));
  const nextRoutineDate = resource?.localStartDate
    ? addDays(new Date(resource.localStartDate), resource.duration + 1)
    : undefined;
  const minDuration = isEditing && !isNextRoutine ? elapsedDays : 1;
  const daysRemainingSeverity =
    getRoutineDaysSeverity(form?.localStartDate, form?.duration, !!currentRoutine?.nextRoutine, !!currentRoutine?.isExpired) ??
    'success';
  const isEndingSoon = hasStarted && !hasEnded && daysRemainingSeverity !== 'success';

  const { data: exercisePreloadValues, isLoading: isLoadingExercisePreloadValues } = useSWR(
    form.externalUser?.id ? `/external-users/${form.externalUser.id}/exercise-preload-values` : null,
    get<RoutineExerciseHashMap>,
  );

  const title = useMemo(() => getRoutinePanelTitle(isCreating, isEditing), [isCreating, isEditing]);

  useEffect(() => {
    if (visible) {
      if (overrideNextRoutine) {
        setCurrentRoutine(resource?.nextRoutine);
      } else {
        setCurrentRoutine(resource);
      }
    }
  }, [resource, visible, overrideNextRoutine]);

  useEffect(() => {
    if (isEditing) {
      const updateWidth = () => {
        setExerciseListWidth(exerciseHeaderRef.current?.offsetWidth ?? 0);
      };

      // Actualizar ancho inicial
      updateWidth();

      // Añadir listener para cuando cambie el tamaño de la ventana
      window.addEventListener('resize', updateWidth);

      // Limpiar el listener al desmontar
      return () => {
        window.removeEventListener('resize', updateWidth);
      };
    }
  }, [isEditing, exerciseHeaderRef.current]);

  const exercises = useRoutineExercises({
    form,
    setForm,
    setFormFields,
    organizationExercises,
    exercisePreloadValues,
    expandedExerciseIds,
    setExpandedExerciseIds,
  });
  const supersets = useRoutineSupersets({ form, setFormFields, organizationExercises });
  const days = useRoutineDays({ form, setForm, expandedExerciseIds, setExpandedExerciseIds });
  const handleDragEnd = useRoutineDragEnd({ form, setForm, expandedExerciseIds, setExpandedExerciseIds });
  const requests = useRoutineRequests({
    form,
    organization,
    user,
    post,
    patch,
    currentRoutine,
    resource,
    isCreating,
    isEditing,
    isNextRoutine,
    hasNextRoutine,
    hasChanged,
    validate,
    setIsSaving,
    resetForm,
    onSaveSuccess,
    onHide,
    onDeleteResource,
    setSelectedExerciseMuscleGroups,
    setSelectedExerciseCategories,
  });

  const goBack = () => {
    onIsEditingChange(false);
    setCurrentRoutine(resource);
    setCreatingNewRoutineForExpired(false);
  };

  const handleGoBack = () => {
    if (hasChanged) {
      confirmDialog({
        message: '¿Deseas regresar a la rutina anterior? Perderás los cambios no guardados.',
        header: 'Regresar a la rutina anterior',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sí, regresar',
        rejectLabel: 'No, continuar editando',
        accept: goBack,
        acceptClassName: 'p-button p-button-text',
        rejectClassName: 'p-button-secondary p-button-text',
        draggable: false,
        resizable: false,
      });
    } else {
      goBack();
    }
  };

  const rowProps: DayItemSharedProps = {
    form,
    isEditing,
    isCreating,
    validationErrors,
    locale,
    organizationExercises,
    selectedExerciseMuscleGroups,
    selectedExerciseCategories,
    isLoadingExercisePreloadValues,
    expandedExerciseIds,
    setExpandedExerciseIds,
    exercises,
    supersets,
    days,
    exerciseHeaderRef,
    exerciseListWidth,
  };

  const renderContent = () => {
    return (
      <form
        className={classNames('routine-form', {
          'form-loading': isSaving,
        })}
      >
        <RoutineTypeField
          form={form}
          setFormFields={setFormFields}
          isCreating={isCreating}
          isNextRoutine={isNextRoutine}
          isEditing={isEditing}
          creatingFromUserProfile={creatingFromUserProfile}
          creatingNewRoutineForExpired={creatingNewRoutineForExpired}
        />

        <RoutineNameField
          form={form}
          setFormFields={setFormFields}
          validationErrors={validationErrors}
          isCreating={isCreating}
          isEditing={isEditing}
          isNextRoutine={isNextRoutine}
        />

        {form?.routineType === ROUTINE_TYPES.USER && (
          <RoutineUserField
            form={form}
            setFormFields={setFormFields}
            validationErrors={validationErrors}
            isCreating={isCreating}
            isEditing={isEditing}
            isNextRoutine={isNextRoutine}
            creatingFromUserProfile={creatingFromUserProfile}
            creatingNewRoutineForExpired={creatingNewRoutineForExpired}
            currentRoutine={currentRoutine}
            resource={resource}
            hasEnded={hasEnded}
            hasNextRoutine={hasNextRoutine}
            userHasEditFeature={userHasEditFeature}
            nextRoutineDate={nextRoutineDate}
            setCurrentRoutine={setCurrentRoutine}
            setForm={setForm}
            setCreatingNewRoutineForExpired={setCreatingNewRoutineForExpired}
          />
        )}

        {form?.routineType === ROUTINE_TYPES.USER && (
          <>
            <StartDateField
              form={form}
              setFormFields={setFormFields}
              validationErrors={validationErrors}
              canEditStartDate={canEditStartDate}
              formattedStartDate={formattedStartDate}
            />
          </>
        )}

        {form?.routineType === ROUTINE_TYPES.USER && (
          <DurationField
            form={form}
            setFormFields={setFormFields}
            validationErrors={validationErrors}
            isCreating={isCreating}
            isEditing={isEditing}
            minDuration={minDuration}
            locale={locale}
            currentRoutine={currentRoutine}
          />
        )}

        {form.routineType === ROUTINE_TYPES.USER && (
          <RoutineStatusField
            form={form}
            currentRoutine={currentRoutine}
            daysDifference={daysDifference}
            elapsedDays={elapsedDays}
            hasStarted={hasStarted}
            hasEnded={hasEnded}
            isCreating={isCreating}
            isEditing={isEditing}
            isNextRoutine={isNextRoutine}
            isEndingSoon={isEndingSoon}
            daysRemainingSeverity={daysRemainingSeverity}
            formattedStartDate={formattedStartDate}
            formattedEndDate={formattedEndDate}
            onFinishRoutine={requests.handleFinishRoutine}
          />
        )}

        {form?.routineType === ROUTINE_TYPES.USER && (
          <RoutineDescriptionField form={form} setFormFields={setFormFields} isEditing={isEditing} isCreating={isCreating} />
        )}

        {(isCreating || isEditing) && (
          <CopyRoutineField
            form={form}
            setFormFields={setFormFields}
            currentRoutine={currentRoutine}
            exercisePreloadValues={exercisePreloadValues}
            isLoadingExercisePreloadValues={isLoadingExercisePreloadValues}
          />
        )}

        <ExerciseDaysField
          {...rowProps}
          onDragEnd={handleDragEnd}
          setSelectedExerciseMuscleGroups={setSelectedExerciseMuscleGroups}
          setSelectedExerciseCategories={setSelectedExerciseCategories}
        />
      </form>
    );
  };

  const handleHide = () => {
    // Resetear el estado de ejercicios expandidos al cerrar el panel
    setExpandedExerciseIds({});

    // Resetear el estado de edición de día
    days.setEditingDayId(null);
    days.setEditingDayName('');

    // Resetear el estado de creación para rutina expirada
    setCreatingNewRoutineForExpired(false);

    // Resetear los filtros
    setSelectedExerciseMuscleGroups([]);
    setSelectedExerciseCategories([]);

    // Llamar a la función onHide proporcionada por el componente padre
    onHide();
  };

  return (
    <Panel
      panelType='resourcePanel'
      resourceName={isNextRoutine ? getRoutineDisplayName(form.name, true) : 'rutina'}
      HeaderComponent={
        <RoutinePanelHeader
          isNextRoutine={isNextRoutine}
          creatingNewRoutineForExpired={creatingNewRoutineForExpired}
          onGoBack={handleGoBack}
          title={title}
          hasEnded={hasEnded}
          currentRoutine={currentRoutine}
          isCreating={isCreating}
          form={form}
          daysDifference={daysDifference}
          isEndingSoon={isEndingSoon}
          daysRemainingSeverity={daysRemainingSeverity}
        />
      }
      visible={visible}
      onIsEditingChange={onIsEditingChange}
      onHide={handleHide}
      onDelete={onDeleteResource ? requests.handleDelete : undefined}
      isDeleting={isDeleting}
      onSave={requests.handleSave}
      hasChanged={hasChanged}
      isSaving={isSaving}
      isEditing={isEditing}
      isCreating={isCreating}
      className='routine-panel'
      resetForm={resetForm}
      hideEditButton={hasEnded}
      allowedFeatures={allowedFeatures}
      customDeleteConfirmationDialogProps={getRoutineDeleteConfirmationDialogProps(resource, isNextRoutine)}
      fullWidth={true}
    >
      {renderContent()}
    </Panel>
  );
};
